import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import classNames from 'classnames'
import upperFirst from 'lodash/upperFirst'
import mapboxgl from 'mapbox-gl'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Offense } from 'src/api/gen'
import { DEVICES_ICONS } from 'src/components/Devices'

export interface IClickedOffensePoint {
  coordinates: mapboxgl.LngLat
  offense: Offense
}

interface IProps extends WithStyles<typeof styles> {
  offense: Offense
}

const OffensePopup = ({ classes, offense }: IProps) => {
  const { t } = useTranslation()

  const providerImage = `url(${offense.device_type ? DEVICES_ICONS[offense.device_type] : '/img/img-placeholder.svg'})`

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <div style={{ backgroundImage: providerImage }} className={classes.media} />
      </div>
      <div className={classes.vertLine} />
      <div style={{ width: '100%' }}>
        <div className={classes.row}>
          <div className={classes.header}>
            <div className={classNames(classes.row, classes.title)}>{upperFirst(offense.provider_name)}</div>
            <div className={classes.vin}>
              <a href={`/vehicles/${offense.device_id}`}>{upperFirst(offense.vin)}</a>
            </div>
            <div className={classes.policyKind}>{upperFirst(t('policy.kind.' + offense.policy_type))}</div>
            <div>
              <a className={classes.policyName} href={`/policy/${offense.policy_id}/violation_list`}>
                {upperFirst(offense.policy_name)}
              </a>
            </div>
            <div className={classes.content}>
              <AccessTimeIcon className={classes.icon} />
              {upperFirst(offense.official_start_date?.toString().split('T')[0]) +
                ' - ' +
                offense.official_start_date?.toString().split('T')[1].split('.')[0]}
            </div>
            <div className={classes.row} style={{ height: 5 }} />
          </div>
        </div>
        <div className={classes.row} style={{ height: 10 }} />
        <div className={classes.row}></div>
      </div>
    </div>
  )
}

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 320,
      padding: '4px',
    },
    media: {
      textAlign: 'center',
      minHeight: 80,
      minWidth: 80,
      marginLeft: theme.spacing(),
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    header: {
      width: '100%',
      maxWidth: '200px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '20px',
      lineHeight: '30px',
      color: theme.palette.grey[800],
      cursor: 'pointer',
    },
    title: {
      fontSize: '18px',
      lineHeight: '20px',
      fontWeight: 'bold',
    },
    vin: {
      fontSize: 15,
      color: theme.palette.grey[500],
      fontWeight: 'bold',
      textDecoration: 'underline',
    },
    content: {
      fontSize: 15,
      color: theme.palette.grey[800],
      display: 'inline-flex',
      verticalAlign: 'text-bottom',
    },
    policyKind: {
      fontSize: 15,
      color: theme.palette.grey[800],
      display: 'inline-flex',
      verticalAlign: 'text-bottom',
    },
    policyName: {
      fontSize: 15,
      color: theme.palette.grey[800],
      display: 'inline-flex',
      verticalAlign: 'text-bottom',
      fontStyle: 'italic',
      textDecoration: 'underline',
    },
    icon: {
      marginRight: theme.spacing(),
      marginTop: 2,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    vertLine: {
      height: 100,
      width: 2,
      background: '#e8e7e7',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  })

export default withStyles(styles)(OffensePopup)
